import type { AppPlan } from '@/webapp/plan/planTypes';
import type { StyleBrief } from '@/llm/planTypes';

/**
 * The build somebody started, kept across app launches so a finished one can be recovered.
 *
 * The server stores a completed build under its idempotency key and replays it
 * (`replayed: true`, webBuild.ts) — so a retry carrying the SAME key gets the finished app
 * back in a fraction of a second, with no model call and no second charge.
 *
 * What that recovers, precisely: a build the server FINISHED while this app never heard the
 * answer — the phone dropped the connection, the app was closed or killed mid-build, or this
 * client gave up waiting while the Lambda kept going. The server writes its receipt before it
 * responds, so the answer exists and only the key can ask for it.
 *
 * A Lambda killed at its own time limit is the other case, and it is recovered differently.
 * That process dies before it writes a receipt or gives the key back, so there is nothing to
 * replay: the key sits at `running`, and a retry under it is told `409 build_in_progress` —
 * which `buildOnServer` waits on. Once the claim is older than the Lambda's limit plus a
 * minute (`claimAbandoned` in webBuild.ts, about eleven minutes), nothing can still be
 * working on it, so the next request under the key TAKES IT OVER and builds again. The
 * takeover carries the dead build's charge rather than taking a new one: the intent is still
 * paid for once. That is a new generation, not the first one's answer, which is lost with the
 * process that was killed.
 *
 * The key was a module variable. It survived `retry()` inside one session and died with the
 * process, so a lost answer was unrecoverable after any restart: the next launch minted a
 * new key, and the finished app sat on the server, paid for and unreachable.
 */
export interface BuildIntentRecord {
  appName: string;
  goal: string;
  signedIn: boolean;
  hadAttachment: boolean;
  /** The idempotency key. Without it there is nothing to replay. */
  requestKey?: string;
  /** The project reserved for this intent, so a recovered app fills the row already shown. */
  reservedId?: string;
  /** When the intent's key was minted, so a key the server has forgotten can be discarded. */
  startedAt?: string;
  /**
   * The person has already been told this attempt ended — by the error screen, or by a launch
   * that found it interrupted.
   *
   * A flag rather than deleting the record, which is what used to happen, and which threw the
   * key and the reserved row away at the exact moment they were needed: the next "Try again"
   * minted a new key (a second charge, no replay) and reserved a second row next to the first.
   */
  reported?: boolean;
  /**
   * The server built this and is holding it for the trial (the web's build before the card), which
   * it keeps for longer than an ordinary key: HELD_REPLAY_WINDOW_MS.
   */
  held?: boolean;
  /**
   * The plan and the look the person chose, kept here because the wizard does not persist them.
   *
   * `start()` read both from the wizard's memory, which is empty in a new process. So after a
   * restart the "Try again" that `resume()` invites, with the words "Your idea is saved", built
   * from the one-line goal alone: no features, no layout, no rules, no reference-picture look.
   * Both are small JSON, unlike the pictures the look was derived from.
   */
  plan?: AppPlan | null;
  styleBrief?: StyleBrief | null;
  /**
   * The catalog app this intent's page was published as, written down as soon as it exists.
   *
   * Hosting creates the catalog app and makes its page public INSIDE the build, before
   * `finalizeBuild` saves anything. If the next step failed, or the app was killed, the id lived
   * only in memory, so Try again created a second catalog app at `<slug>-2` and the first page
   * stayed public with nothing on the phone pointing at it. Passed back to hosting, it replaces
   * that page rather than adding a second one.
   */
  hostedAppId?: string;
  /**
   * The catalog id the reserved ROW is known to carry — set once the row has been written.
   *
   * What it answers is whether deleting that row took the page down. Delete unpublishes the id
   * a row carries before it removes the row (`runProjectDeletion`), and an unpublished app is
   * private: the server takes a content-only publish to it and keeps its page off. So when the
   * row is gone and it carried `hostedAppId`, that id is a page its owner took down, and a
   * rebuild of the same idea handed it finished "live" at an address serving nothing.
   *
   * An id only the record knows (hosting minted it and the app was killed before it returned)
   * was never on the row, so Delete could not have taken it down — that page may be public
   * with nothing pointing at it, and the next build of this idea should adopt it. Absent,
   * the id is kept: the safe reading, because hosting reports a page that stays off.
   */
  rowHostedAppId?: string;
  /**
   * What this attempt was built FROM — the plan, the look and the vibe — as a fingerprint
   * (`intentChoices`). Decides whether the key may be reused; see `resumableIntent`.
   */
  choices?: string;
}

/** How long the server keeps a claimed key. Past this there is nothing to replay. */
const REPLAY_WINDOW_MS = 24 * 3600_000;
/** How long the server keeps an app built before the card (HELD_RECEIPT_SECONDS in webBuild.ts). */
const HELD_REPLAY_WINDOW_MS = 3 * 24 * 3600_000;

/**
 * Whether a stored record describes the build being started now.
 *
 * The record now outlives a failure, a dismissal and a restart, so the next `start()` may be
 * for a different idea entirely. Reusing the old key there would ask the server for the OLD
 * app — a replay of something the person has moved on from — and reusing the old row would
 * write the new app over it. Same name, same sentence: same intent.
 *
 * Same APP, that is: its row and its page. Whether the old KEY may be reused also depends on
 * the choices it was built from — see `resumableIntent`.
 */
export function sameIntent(
  record: Pick<BuildIntentRecord, 'appName' | 'goal'> | null | undefined,
  current: { appName: string; goal: string },
): boolean {
  if (!record) return false;
  return record.appName.trim() === current.appName.trim() && record.goal.trim() === current.goal.trim();
}

/**
 * A fingerprint of what a build is made from, beyond its name and sentence.
 *
 * Order-independent (object keys are sorted), because the same plan arrives from the wizard's
 * memory, from the record after a JSON round trip and from the reserved row, and only a
 * CHANGE of choice may read as a different build.
 */
export function intentChoices(choices: {
  plan?: AppPlan | null;
  styleBrief?: StyleBrief | null;
  vibe?: string | null;
}): string {
  return fingerprintOf({
    plan: choices.plan ?? null,
    styleBrief: choices.styleBrief ?? null,
    vibe: choices.vibe?.trim() || null,
  });
}

/** A short digest of a JSON value, the same for the same value whatever its key order. */
export function fingerprintOf(value: unknown): string {
  const text = stableJson(value);
  // cyrb53: 53 bits is plenty to tell one person's choices from their previous ones.
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map((v) => (v === undefined ? 'null' : stableJson(v))).join(',')}]`;
  if (value && typeof value === 'object') {
    // Undefined members are skipped, as JSON.stringify skips them: a round trip drops them.
    const entries = Object.keys(value as Record<string, unknown>)
      .filter((k) => (value as Record<string, unknown>)[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableJson((value as Record<string, unknown>)[k])}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

/**
 * Whether a record was built from these choices.
 *
 * Unknown on either side counts as the same: a record written before choices were recorded,
 * or a caller that does not know them (the web client), keeps the name-and-sentence rule it
 * had, rather than throwing away a key that may hold a finished, paid-for build.
 */
export function sameChoices(
  record: Pick<BuildIntentRecord, 'choices'>,
  choices: string | undefined,
): boolean {
  return !record.choices || !choices || record.choices === choices;
}

/**
 * The intent worth resuming, or nothing.
 *
 * A record with no key is not resumable: retrying without one is not a recovery, it is a
 * second build — charged again, and racing the first.
 *
 * Given `current`, a record for a DIFFERENT idea is not resumable either (see `sameIntent`),
 * and nor is one built from different CHOICES. The server replays whatever it stored under a
 * key without comparing what it is sent, so a Build with the same name and sentence but new
 * features, a new layout or a new look, sent under the old key, came back as the OLD app —
 * the person's new choices silently dropped. The row and the page still belong to the same
 * app (`sameIntent`); the key belongs to one exact set of choices.
 */
export function resumableIntent(
  record: BuildIntentRecord | null | undefined,
  current?: { appName: string; goal: string; choices?: string },
): BuildIntentRecord | null {
  if (!record?.requestKey) return null;
  if (current && (!sameIntent(record, current) || !sameChoices(record, current.choices))) return null;
  if (record.startedAt) {
    const age = Date.now() - Date.parse(record.startedAt);
    // NaN from an unparseable date falls through as resumable, for the reason below.
    if (Number.isFinite(age) && age > (record.held ? HELD_REPLAY_WINDOW_MS : REPLAY_WINDOW_MS)) return null;
  }
  /*
   * An unknown age is treated as resumable. Older installs wrote records without
   * `startedAt`, and the worst case here is one wasted replay attempt that comes back as an
   * ordinary build — against the alternative, which is losing a build somebody paid for.
   */
  return record;
}
